import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import {
  fetchPrimeArtifactStates,
  fetchPrimeProcesses,
} from '../../store/actions/primeArtifactActions';
import { ArtifactStateTransitionType, DemandType, DemandFlowType } from '../../types/enums';
import { toast } from 'react-hot-toast';
import { Card, List, Tag, Space, Typography, Alert, Button } from 'antd';
import { LinkOutlined, ExclamationCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';
import styles from './PrimeTransitionForm.module.css';

interface PrimeTransitionFormProps {
  prime_artifact_id: string;
  primeStates: unknown[];
  primeProcesses: unknown[];
  initialData: unknown[];
  onSubmit: (data: unknown) => void;
  onBack: () => void;
}

const PrimeTransitionForm: React.FC<PrimeTransitionFormProps> = ({
  prime_artifact_id,
  primeStates,
  primeProcesses,
  initialData,
  onSubmit,
  onBack,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [formData, setFormData] = useState<Record<string, string>>({
    name: '',
    description: '',
    from_state: '',
    to_state: '',
    process: '',
    transition_type: ArtifactStateTransitionType.OPERATIONAL,
    demand_type: DemandType.TARGET,
    demand_flow_type: DemandFlowType.DRUM,
    transition_parameters: '{}',
  });

  const [gptSuggestions, setGptSuggestions] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [propertyEffects, setPropertyEffects] = useState<any[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);

  // Get states and processes from Redux store
  const artifactStates = useSelector(
    (state: RootState) => state.primeArtifacts.primeArtifactStates
  );
  const artifactProcesses = useSelector((state: RootState) => state.primeArtifacts.primeProcesses);

  // Fetch states and processes when component mounts
  // Fetch property effects for the target state
  const fetchPropertyEffects = async (toStateName: string) => {
    if (!toStateName) {
      setPropertyEffects([]);
      return;
    }

    try {
      setLoadingProperties(true);
      // Find the state ID by name
      const targetState = availableStates.find((state: any) => state.name === toStateName) as any;
      if (!targetState || !targetState.id) {
        setPropertyEffects([]);
        return;
      }

      const response = await apiClient.get(`/prime-artifact-states/${targetState.id}/properties`);
      const properties = response.data.properties || [];
      
      // Filter for properties that could be affected by this transition
      const materialProperties = properties.filter((prop: any) => prop.source === 'material');
      setPropertyEffects(materialProperties);
    } catch (error) {
      console.error('Error fetching property effects:', error);
      setPropertyEffects([]);
    } finally {
      setLoadingProperties(false);
    }
  };

  useEffect(() => {
    if (prime_artifact_id) {
      void dispatch(fetchPrimeArtifactStates(prime_artifact_id));
      void dispatch(fetchPrimeProcesses(prime_artifact_id));
    }
  }, [dispatch, prime_artifact_id]);

  // Fetch property effects when target state changes
  useEffect(() => {
    if (formData.to_state) {
      fetchPropertyEffects(formData.to_state);
    }
  }, [formData.to_state]);

  // Initialize form with initial data and GPT suggestions
  useEffect(() => {
    if (initialData && initialData.length > 0) {
      const data = initialData[0] as Record<string, unknown>;
      console.log('Initial data for process:', {
        process_name: data.process_name,
        process: data.process,
        data: data,
      });

      const dataObj = data as Record<string, string>;
      setFormData({
        name: dataObj.name || '',
        description: dataObj.description || '',
        from_state: dataObj.from_state_name || '',
        to_state: dataObj.to_state_name || '',
        process: dataObj.process_name || '',
        transition_type: dataObj.transition_type || ArtifactStateTransitionType.OPERATIONAL,
        demand_type: dataObj.demand_type || DemandType.TARGET,
        demand_flow_type: dataObj.demand_flow_type || DemandFlowType.DRUM,
        transition_parameters: dataObj.transition_parameters || '{}',
      });

      // Store GPT suggestions for all fields
      setGptSuggestions({
        name: dataObj.name || '',
        description: dataObj.description || '',
        from_state: dataObj.from_state_name || '',
        to_state: dataObj.to_state_name || '',
        process: dataObj.process_name || dataObj.process || '',
        transition_type: dataObj.transition_type || '',
        demand_type: dataObj.demand_type || '',
        demand_flow_type: dataObj.demand_flow_type || '',
      });
    }
  }, [initialData]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev: Record<string, string>) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Find the selected states and process
      const fromState = artifactStates.find(
        (s: unknown) => (s as { name: string }).name === formData.from_state
      );
      const toState = artifactStates.find(
        (s: unknown) => (s as { name: string }).name === formData.to_state
      );
      const process = artifactProcesses.find(
        (p: unknown) => (p as { name: string }).name === formData.process
      );

      if (!fromState || !toState || !process) {
        throw new Error('Please select valid states and process');
      }

      const transitionData = {
        name: `Transition from ${formData.from_state} to ${formData.to_state}`,
        prime_artifact_ref: prime_artifact_id,
        from_state_ref: fromState.id,
        to_state_ref: toState.id,
        prime_process_ref: process.id,
        default_transition_type: formData.transition_type,
        demand_type: formData.demand_type,
        demand_flow_type: formData.demand_flow_type,
        transition_parameters: formData.transition_parameters || {},
        validation_rules: {},
        improvement_ref: null,
        improvement_parameters: {},
      };

      await onSubmit(transitionData);
      toast.success('Transition created successfully');
    } catch (error: unknown) {
      console.error('Error creating transition:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create transition');
    } finally {
      setIsLoading(false);
    }
  };

  const renderLabel = (field: string, label: string) => {
    const suggestion = gptSuggestions[field];
    return suggestion ? `${label} (${suggestion})` : label;
  };

  // Use both Redux store data and props for dropdowns
  const availableStates = Array.from(new Set([...artifactStates, ...primeStates]));
  const availableProcesses = Array.from(new Set([...artifactProcesses, ...primeProcesses]));

  const transitionTypeOptions = [
    { value: ArtifactStateTransitionType.OPERATIONAL, label: 'Operational' },
    { value: ArtifactStateTransitionType.MATERIAL, label: 'Material' },
    { value: ArtifactStateTransitionType.RESOURCE, label: 'Resource' },
    { value: ArtifactStateTransitionType.CREATION, label: 'Creation' },
    { value: ArtifactStateTransitionType.IMPROVEMENT, label: 'Improvement' },
  ];

  const demandTypeOptions = [
    { 
      value: DemandType.TARGET, 
      label: 'Target', 
      description: 'A target value or rate is entered for this item' 
    },
    { 
      value: DemandType.CAPACITY, 
      label: 'Capacity', 
      description: "The resource's capacity will determine demand rate for this item" 
    },
    { 
      value: DemandType.SUPPLY, 
      label: 'Supply (default)', 
      description: 'Demand is set by the supply flowing into this item' 
    },
  ];

  const demandFlowTypeOptions = [
    { 
      value: DemandFlowType.DRUM, 
      label: 'Drum', 
      description: "This item's demand sets the demand flow" 
    },
    { 
      value: DemandFlowType.PULL, 
      label: 'Pull (default)', 
      description: 'This item will pull demand as needed' 
    },
    { 
      value: DemandFlowType.PUSH, 
      label: 'Push', 
      description: 'Any demand from this item will push to the next transition' 
    },
  ];

  return (
    <form onSubmit={handleSubmit} className={styles.transitionForm}>
      <div className={styles.formGroup}>
        <label>{renderLabel('name', 'Name')}</label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          required
        />
      </div>

      <div className={styles.formGroup}>
        <label>{renderLabel('description', 'Description')}</label>
        <textarea
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
        />
      </div>

      <div className={styles.formGroup}>
        <label>{renderLabel('from_state', 'From State')}</label>
        <select
          value={formData.from_state}
          onChange={(e) => handleChange('from_state', e.target.value)}
          required
        >
          <option value="">Select a state</option>
          {availableStates.map((state: unknown) => (
            <option key={(state as { id: string }).id} value={(state as { name: string }).name}>
              {(state as { name: string }).name}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.formGroup}>
        <label>{renderLabel('to_state', 'To State')}</label>
        <select
          value={formData.to_state}
          onChange={(e) => handleChange('to_state', e.target.value)}
          required
        >
          <option value="">Select a state</option>
          {availableStates.map((state: unknown) => (
            <option key={(state as { id: string }).id} value={(state as { name: string }).name}>
              {(state as { name: string }).name}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.formGroup}>
        <label>{renderLabel('process', 'Process')}</label>
        <select
          value={formData.process as string}
          onChange={(e) => handleChange('process', e.target.value)}
          required
        >
          <option value="">Select a process</option>
          {availableProcesses.map((process: unknown) => (
            <option key={(process as { id: string }).id} value={(process as { name: string }).name}>
              {(process as { name: string }).name}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.formGroup}>
        <label>{renderLabel('transition_type', 'Transition Type')}</label>
        <select
          value={formData.transition_type as string}
          onChange={(e) => handleChange('transition_type', e.target.value)}
        >
          {transitionTypeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.formGroup}>
        <label>{renderLabel('demand_type', 'Demand Type')}</label>
        <small style={{ display: 'block', color: '#666', marginBottom: '4px' }}>
          How demand is set for this item
        </small>
        <select
          value={formData.demand_type as string}
          onChange={(e) => handleChange('demand_type', e.target.value)}
          title={demandTypeOptions.find(opt => opt.value === formData.demand_type)?.description || 'Select demand type'}
        >
          {demandTypeOptions.map((option) => (
            <option key={option.value} value={option.value} title={option.description}>
              {option.label}
            </option>
          ))}
        </select>
        <small style={{ display: 'block', color: '#888', marginTop: '4px', fontStyle: 'italic' }}>
          {demandTypeOptions.find(opt => opt.value === formData.demand_type)?.description || 'Select a demand type'}
        </small>
      </div>

      <div className={styles.formGroup}>
        <label>{renderLabel('demand_flow_type', 'Demand Flow Type')}</label>
        <small style={{ display: 'block', color: '#666', marginBottom: '4px' }}>
          How demand flows through this transition
        </small>
        <select
          value={formData.demand_flow_type as string}
          onChange={(e) => handleChange('demand_flow_type', e.target.value)}
          title={demandFlowTypeOptions.find(opt => opt.value === formData.demand_flow_type)?.description || 'Select demand flow type'}
        >
          {demandFlowTypeOptions.map((option) => (
            <option key={option.value} value={option.value} title={option.description}>
              {option.label}
            </option>
          ))}
        </select>
        <small style={{ display: 'block', color: '#888', marginTop: '4px', fontStyle: 'italic' }}>
          {demandFlowTypeOptions.find(opt => opt.value === formData.demand_flow_type)?.description || 'Select a demand flow type'}
        </small>
      </div>

      {/* Property Effects Section */}
      {formData.to_state && (
        <div className={styles.propertyEffectsSection}>
          <Card 
            title={
              <Space>
                <LinkOutlined />
                <span>Property Effects</span>
                {propertyEffects.length > 0 && (
                  <Tag color="blue">{propertyEffects.length} properties</Tag>
                )}
              </Space>
            }
            size="small"
          >
            {loadingProperties ? (
              <div>Loading property effects...</div>
            ) : propertyEffects.length > 0 ? (
              <List
                size="small"
                dataSource={propertyEffects}
                renderItem={(property: any) => (
                  <List.Item>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Space>
                        <Typography.Text strong>{property.name}</Typography.Text>
                        <Tag color="purple">{property.type}</Tag>
                        {property.linked_transition_ref ? (
                          <Tag color="green" icon={<CheckCircleOutlined />}>
                            Linked
                          </Tag>
                        ) : (
                          <Tag color="orange" icon={<ExclamationCircleOutlined />}>
                            Needs Link
                          </Tag>
                        )}
                      </Space>
                      <Typography.Text type="secondary">
                        {property.description}
                      </Typography.Text>
                      {property.material_artifact_name && (
                        <Typography.Text code>
                          → {property.material_artifact_name} ({property.condition?.state})
                        </Typography.Text>
                      )}
                    </Space>
                  </List.Item>
                )}
              />
            ) : (
              <Alert
                message="No Material Properties"
                description="The target state doesn't have any material properties that would be affected by this transition."
                type="info"
                showIcon
              />
            )}
          </Card>
        </div>
      )}

      <div className={styles.formActions}>
        <button type="button" onClick={onBack} disabled={isLoading}>
          Back
        </button>
        <button type="submit" disabled={isLoading}>
          {isLoading ? 'Creating...' : 'Create Transition'}
        </button>
      </div>
    </form>
  );
};

export default PrimeTransitionForm;
